import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles, Download, Trash2, Loader2, FileText, CheckCircle, XCircle, AlertCircle, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ClaimImportDialog } from "@/components/job-detail/claims/ClaimImportDialog";
import { ClaimEstimateCard } from "@/components/job-detail/claims/ClaimEstimateCard";
import { ClaimVsEstimateView } from "@/components/job-detail/claims/ClaimVsEstimateView";

interface SupplementLineItem {
  id: string;
  item_name: string;
  description: string | null;
  quantity: number;
  unit_type: string | null;
  unit_price: number;
  total_cost: number;
  is_ai_suggested: boolean;
  category: string | null;
}

interface AISuggestion {
  itemCode: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalCost: number;
  justification: string;
}

interface SupplementsTabProps {
  jobId: string;
  orgId: string;
}

export const SupplementsTab = ({ jobId, orgId }: SupplementsTabProps) => {
  const [items, setItems] = useState<SupplementLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  
  // Damage and claim summary
  const [photosAnalyzed, setPhotosAnalyzed] = useState(0);
  const [photosWithDamage, setPhotosWithDamage] = useState(0);
  const [damageTypes, setDamageTypes] = useState<string[]>([]);
  const [claimData, setClaimData] = useState<any>(null);
  const [estimates, setEstimates] = useState<any[]>([]);

  // Adjuster claim PDFs
  const [claimEstimates, setClaimEstimates] = useState<Array<{ id: string; carrier: string | null; source_format: string; rcv_total: number | null; created_at: string }>>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [comparingId, setComparingId] = useState<string | null>(null);

  useEffect(() => {
    loadAllData();
  }, [jobId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSupplementItems(),
        loadPhotoAnalysis(),
        loadClaimAnalysis(),
        loadEstimates(),
        loadClaimEstimates(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadClaimEstimates = async () => {
    const { data, error } = await supabase
      .from("claim_estimates")
      .select("id, carrier, source_format, rcv_total, created_at")
      .eq("job_id", jobId)
      .order("created_at", { ascending: false });
    if (!error) setClaimEstimates((data as any) || []);
  };

  const loadSupplementItems = async () => {
    try {
      const { data, error } = await supabase
        .from("supplement_line_items")
        .select("*")
        .eq("job_id", jobId)
        .order("line_order", { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error: any) {
      console.error("Error loading supplement items:", error);
      toast.error("Failed to load supplement items");
    }
  };

  const loadPhotoAnalysis = async () => {
    try {
      const { data, error } = await supabase
        .from("photos")
        .select("id, ai_analyzed, ai_damage_detected, ai_damage_types")
        .eq("job_id", jobId);

      if (error) throw error;
      
      const analyzed = data?.filter(p => p.ai_analyzed).length || 0;
      const withDamage = data?.filter(p => p.ai_damage_detected).length || 0;
      const types = data?.reduce((acc: string[], p) => {
        if (p.ai_damage_types) {
          acc.push(...p.ai_damage_types);
        }
        return acc;
      }, []);

      setPhotosAnalyzed(analyzed);
      setPhotosWithDamage(withDamage);
      setDamageTypes([...new Set(types || [])]);
    } catch (error: any) {
      console.error("Error loading photo analysis:", error);
    }
  };

  const loadClaimAnalysis = async () => {
    try {
      const { data, error } = await supabase
        .from("claim_analyses")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setClaimData(data);
    } catch (error: any) {
      console.error("Error loading claim analysis:", error);
    }
  };

  const loadEstimates = async () => {
    try {
      const { data, error } = await supabase
        .from("job_estimates")
        .select("*")
        .eq("job_id", jobId);

      if (error) throw error;
      setEstimates(data || []);
    } catch (error: any) {
      console.error("Error loading estimates:", error);
    }
  };

  const handleGenerateSupplements = async () => {
    if (photosAnalyzed === 0) {
      toast.error("No analyzed photos", {
        description: "Please analyze photos before generating supplements"
      });
      return;
    }

    setGenerating(true);
    try {
      // Extract original estimate items
      const originalEstimateItems = estimates.flatMap(est => 
        est.line_items || []
      );

      const { data, error } = await supabase.functions.invoke('generate-supplement', {
        body: { 
          jobId,
          orgId,
          originalEstimateItems
        }
      });

      if (error) throw error;

      if (data?.suggestions && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
        toast.success(`Found ${data.suggestions.length} supplement suggestion(s)`);
      } else {
        toast.info("No additional supplements needed", {
          description: "AI analysis found no gaps in the original estimate"
        });
        setSuggestions([]);
      }
    } catch (error: any) {
      console.error("Error generating supplements:", error);
      toast.error(error.message || "Failed to generate supplements");
    } finally {
      setGenerating(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion: AISuggestion) => {
    try {
      const { error } = await supabase
        .from("supplement_line_items")
        .insert({
          job_id: jobId,
          org_id: orgId,
          item_name: suggestion.itemCode,
          description: `${suggestion.description}\n\nJustification: ${suggestion.justification}`,
          quantity: suggestion.quantity,
          unit_type: suggestion.unit,
          unit_price: suggestion.unitPrice,
          total_cost: suggestion.totalCost,
          is_ai_suggested: true,
          line_order: items.length,
        });

      if (error) throw error;
      
      toast.success("Supplement item added");
      setSuggestions(prev => prev.filter(s => s.itemCode !== suggestion.itemCode));
      await loadSupplementItems();
    } catch (error: any) {
      console.error("Error accepting suggestion:", error);
      toast.error("Failed to add supplement item");
    }
  };

  const handleRejectSuggestion = (suggestion: AISuggestion) => {
    setSuggestions(prev => prev.filter(s => s.itemCode !== suggestion.itemCode));
    toast.info("Suggestion rejected");
  };

  const handleAddItem = async () => {
    setAdding(true);
    try {
      const { error } = await supabase
        .from("supplement_line_items")
        .insert({
          job_id: jobId,
          org_id: orgId,
          item_name: "New Item",
          description: "",
          quantity: 1,
          unit_price: 0,
          total_cost: 0,
          line_order: items.length,
        });

      if (error) throw error;
      toast.success("Item added");
      await loadSupplementItems();
    } catch (error: any) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from("supplement_line_items")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Item deleted");
      await loadSupplementItems();
    } catch (error: any) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.total_cost || 0), 0);

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">AI Supplements Workspace</h2>
          <p className="text-muted-foreground mt-1">
            Generate and manage insurance claim supplements with AI
          </p>
        </div>
        <Button className="bg-action hover:bg-action-hover">
          <Download className="w-4 h-4 mr-2" />
          Export PDF
        </Button>
      </div>

      {/* Adjuster Claim Imports */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Adjuster Claims</CardTitle>
            <CardDescription>
              Upload Xactimate / Symbility / Allstate / State Farm / USAA PDFs to extract line items and compare against your estimate.
            </CardDescription>
          </div>
          <Button onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-2" /> Import Claim PDF
          </Button>
        </CardHeader>
        {claimEstimates.length > 0 && (
          <CardContent className="space-y-4">
            {claimEstimates.map((c) => (
              <div key={c.id} className="space-y-3">
                <ClaimEstimateCard
                  claimEstimateId={c.id}
                  onCompare={(id) => setComparingId(id)}
                  onDeleted={() => { loadClaimEstimates(); if (comparingId === c.id) setComparingId(null); }}
                />
                {comparingId === c.id && (
                  <ClaimVsEstimateView
                    claimEstimateId={c.id}
                    jobId={jobId}
                    orgId={orgId}
                    onClose={() => setComparingId(null)}
                  />
                )}
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      <ClaimImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        jobId={jobId}
        orgId={orgId}
        onParsed={() => { loadClaimEstimates(); loadSupplementItems(); }}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Damage Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Photos analyzed:</span>
                <span className="font-semibold">{photosAnalyzed}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">With damage:</span>
                <span className="font-semibold text-warning">{photosWithDamage}</span>
              </div>
              {damageTypes.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {damageTypes.slice(0, 3).map(type => (
                    <Badge key={type} variant="outline" className="text-xs">
                      {type}
                    </Badge>
                  ))}
                  {damageTypes.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{damageTypes.length - 3}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Claim Information</CardTitle>
          </CardHeader>
          <CardContent>
            {claimData ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">RCV:</span>
                  <span className="font-semibold">${claimData.rcv?.toLocaleString() || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Deductible:</span>
                  <span className="font-semibold">${claimData.deductible?.toLocaleString() || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Policy #:</span>
                  <span className="font-mono text-xs">{claimData.policy_number || '—'}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No claim data available</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Original Estimate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Estimates:</span>
                <span className="font-semibold">{estimates.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Line items:</span>
                <span className="font-semibold">
                  {estimates.reduce((sum, est) => sum + (est.line_items?.length || 0), 0)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Generate Button */}
      {photosAnalyzed > 0 && (
        <Alert className="border-primary/30 bg-primary/5">
          <Sparkles className="h-4 w-4 text-primary" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-sm">
              Ready to analyze damage and generate supplement suggestions based on {photosWithDamage} photos with detected damage
            </span>
            <Button 
              onClick={handleGenerateSupplements} 
              disabled={generating}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-[0_0_30px_rgba(147,51,234,0.3)] hover:shadow-[0_0_40px_rgba(147,51,234,0.5)] ml-4"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate AI Supplements
                </>
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-primary/20 bg-card/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI-Suggested Supplements ({suggestions.length})
            </CardTitle>
            <CardDescription>
              Review and accept/reject supplement suggestions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {suggestions.map((suggestion, idx) => (
                <Card key={idx} className="border-primary/10">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-primary/20 text-primary border-primary/30">
                              {suggestion.itemCode}
                            </Badge>
                            <span className="font-semibold">{suggestion.description}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {suggestion.justification}
                          </p>
                          <div className="flex items-center gap-4 text-sm">
                            <span>Qty: {suggestion.quantity} {suggestion.unit}</span>
                            <span>@ ${suggestion.unitPrice.toFixed(2)}</span>
                            <span className="font-semibold text-primary">
                              Total: ${suggestion.totalCost.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => handleAcceptSuggestion(suggestion)}
                            className="bg-success hover:bg-success/90"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectSuggestion(suggestion)}
                            className="border-destructive/30 text-destructive hover:bg-destructive/10"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Supplement Line Items</CardTitle>
              <CardDescription>Accepted items for your supplement claim</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleAddItem} disabled={adding}>
              {adding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add Manual Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium">Item</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Description</th>
                  <th className="text-right py-3 px-4 text-sm font-medium">Quantity</th>
                  <th className="text-right py-3 px-4 text-sm font-medium">Unit Price</th>
                  <th className="text-right py-3 px-4 text-sm font-medium">Total</th>
                  <th className="text-right py-3 px-4 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      No supplement items yet. Use AI generation or add items manually.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="border-b">
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-2">
                          {item.item_name}
                          {item.is_ai_suggested && (
                            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                              AI
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground max-w-md truncate">
                        {item.description || "-"}
                      </td>
                      <td className="py-3 px-4 text-sm text-right">
                        {item.quantity} {item.unit_type || ""}
                      </td>
                      <td className="py-3 px-4 text-sm text-right">
                        ${item.unit_price.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-medium">
                        ${item.total_cost.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2">
                  <td colSpan={4} className="py-4 px-4 text-right font-semibold">
                    Supplement Total:
                  </td>
                  <td className="py-4 px-4 text-right text-lg font-bold text-primary">
                    ${totalAmount.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      {photosAnalyzed === 0 && (
        <Card className="border-primary/20 bg-card/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <AlertCircle className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Ready to Start?</h3>
                <p className="text-sm text-muted-foreground">
                  Upload and analyze damage photos from the Damage Hub, then return here to generate 
                  AI-powered supplement suggestions based on detected damage and your original estimate.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
